/**
 * IR → BaskReport XML 序列化器（唯一出口，纯函数）。
 *
 * 关键约束（已用真实样本对齐）：
 * - 单元格属性按固定顺序输出（row, col, name, left, top, expand, *-padding,
 *   rowspan, colspan, force-paging-before, force-paging, paging-repeat-content,
 *   row-auto-stretch, column-auto-stretch, shrink-font-to-fill, align, valign,
 *   forecolor, bgcolor?, font-size, font-family, bold?, italic?, underline?,
 *   line-through?, text-direction?, letter-spacing?, visible?）。
 * - 子元素顺序：format? → left/right/top/bottom-border → content → facade
 *   → render-item*（条件样式 render，多个 CellRender 各生成一个 render-item）。
 * - facade：无映射输出 <facade type="none"/>；有映射输出 type="mapping" +
 *   mapping-type="custom|dataset"，custom 用 <custom-mapping-data label value/>，
 *   dataset 用 dataset/value-field/label-field 属性（对齐官方内容渲染器规范）。
 * - 合并：被覆盖格不输出（由 expandGrid 标记 covered）。
 * - 根元素 <baskreport version="1.1">（官方 ReportDefinitionParser.NAME="baskreport"）。
 * - 样式属性名严格遵守官方 CellDefinitionParser 读取的 kebab 键：
 *   align/valign/forecolor/bgcolor/font-size/font-family/bold/italic/underline/
 *   line-through/text-direction/letter-spacing/visible。
 */

import { create } from 'xmlbuilder2';
import type {
  CellIR,
  ReportIR,
  RowIR,
  ColumnIR,
  DatasetIR,
  DatasourceIR,
  PaperIR,
  ParameterIR,
  StyleIR,
  CellBorder,
  ParameterType,
  CellRender,
  FormElementIR,
  WatermarkIR,
} from '../ir/types.js';
import { expandGrid } from '../grid/expand.js';
import { normalizeTopParentCells, resolveParent } from '../grid/parent.js';
import { coordName } from '../grid/coordinate.js';
import { DEFAULTS } from '../grid/defaults.js';

/**
 * editor-type 归一映射：LLM 可能写出多种别名，统一归并为官方支持的有限集合
 * （text / select / date / button），避免生成引擎无法识别的控件类型。
 * 单向映射，未知值原样透传（交由 structureCheck 校验拦截）。
 */
const EDITOR_TYPE_MAP: Record<string, string> = {
  text: 'text',
  textarea: 'text',
  input: 'text',
  select: 'select',
  dropdown: 'select',
  combo: 'select',
  combobox: 'select',
  date: 'date',
  datepicker: 'date',
  calendar: 'date',
  button: 'button',
};

/** 归一并校验 editorType：未知别名回退到传入值（保留以便 structureCheck 报错定位）。 */
function normalizeEditorType(t?: string): string {
  if (!t) return 'text';
  const key = t.toLowerCase();
  return EDITOR_TYPE_MAP[key] ?? t;
}

const border = (b: CellBorder | undefined) => ({
  style: b?.style ?? DEFAULTS.border.style,
  width: b?.width ?? DEFAULTS.border.width,
  color: b?.color ?? DEFAULTS.border.color,
});

function attr(node: any, name: string, value: string | number | boolean) {
  node.att(name, value);
}

function emitBorder(node: any, tag: string, b: CellBorder) {
  const el = node.ele(tag);
  el.att('style', b.style ?? DEFAULTS.border.style);
  el.att('width', b.width ?? DEFAULTS.border.width);
  el.att('color', b.color ?? DEFAULTS.border.color);
  return el;
}

/**
 * 单元格属性映射表（顺序即 XML 输出顺序，已对齐官方 CellDefinitionParser 与测试快照）。
 * - get: 从 CellIR 取值；返回 undefined 且 omitWhenUndefined 为真 → 跳过该属性。
 * - fallback: get 返回 undefined 且非 omitWhenUndefined 时使用的缺省值（总是输出）。
 */
interface CellAttrSpec {
  xml: string;
  get: (cell: CellIR, pos: { left: string; top: string }) => string | number | boolean | undefined;
  fallback?: string | number | boolean;
  omitWhenUndefined?: boolean;
}

// 可展开的 dataset 明细格聚合类型（含已废弃的 none 旧别名——emitDatasetContent
// 会把 none/缺省归并为 select，故 none 也应视为明细、缺省方向为 down）。
const EXPANDABLE_DATASET_AGG = new Set(['select', 'group', 'conditiongroup', 'expressiongroup', 'none']);

const CELL_ATTRS: CellAttrSpec[] = [
  { xml: 'row', get: (c) => c.row },
  { xml: 'col', get: (c) => c.col },
  { xml: 'name', get: (c) => c.name ?? coordName(c.row, c.col) },
  { xml: 'left', get: (_c, pos) => pos.left },
  { xml: 'top', get: (_c, pos) => pos.top },
  {
    // dataset 明细格缺省方向为 down：与校验层 3b-1 的归并保持一致，
    // 避免「校验默认 down、序列化输出 none」的不一致（none 无法确定扩展轴向）。
    xml: 'expand',
    get: (c) => {
      const agg = c.content?.kind === 'dataset' ? (c.content.aggregate ?? 'select') : undefined;
      const isDetail = agg !== undefined && EXPANDABLE_DATASET_AGG.has(agg);
      return c.expand ?? (isDetail ? 'down' : DEFAULTS.expand);
    },
  },
  { xml: 'left-padding', get: (c) => (c.style ? 0 : DEFAULTS.leftPadding) },
  { xml: 'right-padding', get: () => 0 },
  { xml: 'top-padding', get: () => 0 },
  { xml: 'bottom-padding', get: () => 0 },
  { xml: 'rowspan', get: (c) => c.rowspan ?? DEFAULTS.rowspan },
  { xml: 'colspan', get: (c) => c.colspan ?? DEFAULTS.colspan },
  { xml: 'force-paging-before', get: (c) => c.forcePagingBefore ?? DEFAULTS.forcePagingBefore },
  { xml: 'force-paging', get: (c) => c.forcePaging ?? DEFAULTS.forcePaging },
  { xml: 'paging-repeat-content', get: () => DEFAULTS.pagingRepeatContent },
  { xml: 'row-auto-stretch', get: () => DEFAULTS.rowAutoStretch },
  { xml: 'column-auto-stretch', get: () => DEFAULTS.columnAutoStretch },
  { xml: 'shrink-font-to-fill', get: () => DEFAULTS.shrinkFontToFill },
  { xml: 'align', get: (c) => c.style?.align ?? DEFAULTS.align },
  { xml: 'valign', get: (c) => c.style?.valign ?? DEFAULTS.valign },
  { xml: 'forecolor', get: (c) => c.style?.forecolor ?? DEFAULTS.forecolor },
  { xml: 'bgcolor', get: (c) => c.style?.bgcolor, omitWhenUndefined: true },
  { xml: 'font-size', get: (c) => c.style?.fontSize ?? DEFAULTS.fontSize },
  { xml: 'font-family', get: (c) => c.style?.fontFamily ?? DEFAULTS.fontFamily },
  { xml: 'bold', get: (c) => c.style?.bold, omitWhenUndefined: true },
  { xml: 'italic', get: (c) => c.style?.italic, omitWhenUndefined: true },
  { xml: 'underline', get: (c) => c.style?.underline, omitWhenUndefined: true },
  { xml: 'line-through', get: (c) => c.style?.lineThrough, omitWhenUndefined: true },
  { xml: 'text-direction', get: (c) => c.style?.textDirection, omitWhenUndefined: true },
  { xml: 'letter-spacing', get: (c) => c.style?.letterSpacing, omitWhenUndefined: true },
  { xml: 'visible', get: (c) => c.style?.visible, omitWhenUndefined: true },
];

/** 纸张/页面属性映射表（顺序即 XML 输出顺序，已对齐测试快照）。 */
interface PaperAttrSpec {
  xml: string;
  get: (p: PaperIR) => string | number | boolean | undefined;
  fallback: string | number | boolean;
}

const PAPER_ATTRS: PaperAttrSpec[] = [
  { xml: 'paper', get: (p) => p.paper, fallback: DEFAULTS.paper },
  { xml: 'paper-width', get: (p) => p.paperWidth, fallback: DEFAULTS.paperWidth },
  { xml: 'paper-height', get: (p) => p.paperHeight, fallback: DEFAULTS.paperHeight },
  { xml: 'margin-top', get: (p) => p.marginTop, fallback: DEFAULTS.marginTop },
  { xml: 'margin-bottom', get: (p) => p.marginBottom, fallback: DEFAULTS.marginBottom },
  { xml: 'margin-left', get: (p) => p.marginLeft, fallback: DEFAULTS.marginLeft },
  { xml: 'margin-right', get: (p) => p.marginRight, fallback: DEFAULTS.marginRight },
  { xml: 'orientation', get: (p) => p.orientation, fallback: DEFAULTS.orientation },
];

/**
 * IR 参数类型 → BaskReport 参数类型名（首字母大写）。缺省 String。
 * 官方参数类型仅六种：String / Number / Boolean / Date / Datetime / List，
 * 没有 Double（数字用 Number）。
 */
const FIELD_TYPE_MAP: Record<NonNullable<ParameterType>, string> = {
  number: 'Number',
  boolean: 'Boolean',
  string: 'String',
  date: 'Date',
  datetime: 'Datetime',
  list: 'List',
};

function emitStyleAndContent(node: any, cell: CellIR) {
  const s: StyleIR | undefined = cell.style;

  // 数字/日期格式化：仅显式声明 style.format 才输出（官方 FormatType 枚举
  // text/date/currency/number/custom，缺省 number），位于边框之前，见文件头契约。
  if (s?.format !== undefined) {
    const el = node.ele('format');
    el.att('type', s.formatType ?? 'number');
    el.dat(s.format);
  }

  // 边框（缺省四边细黑实线）
  const b = s?.border;
  emitBorder(node, 'left-border', border(b?.left));
  emitBorder(node, 'right-border', border(b?.right));
  emitBorder(node, 'top-border', border(b?.top));
  emitBorder(node, 'bottom-border', border(b?.bottom));

  // 内容
  const content = cell.content;
  if (content) {
    if (content.kind === 'text') {
      const el = node.ele('text-content');
      el.dat(content.value ?? '');
    } else if (content.kind === 'expression') {
      const el = node.ele('expression-content');
      el.att('word-wrap', 'false');
      el.dat(content.value ?? '');
    } else if (content.kind === 'dataset') {
      const el = node.ele('dataset-content');
      el.att('dataset', content.dataset);
      el.att('field', content.field);
      el.att('word-wrap', 'false');
      // aggregate-type 必须显式给出且为合法枚举：
      //   group=分组去重, sum=聚合, select=逐行明细列出（非分组列默认）。
      // IR 类型 AggregateType = 'group'|'sum'|'none'，但引擎 AggregateType.valueOf
      // 对 'none' 及任意未识别/空值都会 NPE，故统一把 none/缺省 归并为 'select'。
      const agg: string =
        content.aggregate && content.aggregate !== 'none' ? content.aggregate : 'select';
      el.att('aggregate-type', agg);
      el.att('enabled-parent-cell-filter', 'true');
      el.att('order', 'none');
      // 表达式分组（aggregate=expressiongroup）：需输出 <group-expression> 分组表达式，
      // 如 formatDate(#,"yyyy") 按年分组；缺省该元素时引擎按原始值分组（退化为普通 group）。
      if (content.aggregate === 'expressiongroup' && content.groupExpression) {
        const ge = el.ele('group-expression');
        ge.dat(content.groupExpression);
      }
      // 主子报表关联条件：每个 condition 生成一个 <condition> 子元素，
      // 使子数据按父字段过滤（如 employee.DEPT_ID == dept.DEPT_ID）。
      for (const c of content.conditions ?? []) {
        const cond = el.ele('condition');
        cond.att('left', c.left);
        cond.att('op', c.op);
        cond.att('value-type', c.valueType);
        if (c.valueType === 'dataset') {
          if (c.dataset) cond.att('dataset', c.dataset);
          if (c.field) cond.att('field', c.field);
        }
        // expression/string 类型把比较值写入文本节点（与官方 XML 一致）。
        if (c.value !== undefined) cond.dat(c.value);
      }
    } else if (content.kind === 'crosstab') {
      const el = node.ele('crosstab-content');
      el.att('direction', content.direction ?? 'down');
      // 角区画布尺寸 = 合并列数 × 默认列宽（80），合并行数 × 默认行高（26）。
      // 与 builder 的角区列宽/行高之和一致，无需 IR 手工配置。
      const width = (cell.colspan ?? 1) * 80;
      const height = (cell.rowspan ?? 1) * 26;
      el.att('width', width.toFixed(1));
      el.att('height', height.toFixed(1));
      // 斜线表头分区 <item>：仅生成分区文字与几何属性（不含 <base64-data> 斜线图）。
      // item 顺序对齐官方：列维度标签在前、行维度标签在后。
      const labels = content.cornerLabels;
      if (labels) {
        const items: CrosstabItemGeo[] = [
          ...labels.col.map((t) => ({ text: t })),
          ...labels.row.map((t) => ({ text: t })),
        ];
        if (items.length > 0) {
          // 角点相对画布坐标（官方 CrosstabRender 减去 2pt 边框余量）。
          const endX = width - 2;
          const endY = height - 2;
          calcItemDetail(items, { xPos: endX, yPos: endY });
          for (const it of items) {
            const itemEl = el.ele('item');
            itemEl.att('degree', it.degree!.toFixed(2));
            itemEl.att('xPos', it.xPos!.toFixed(2));
            itemEl.att('yPos', it.yPos!.toFixed(2));
            itemEl.dat(it.text ?? '', { cdata: true });
          }
        }
      }
    }
  } else {
    // 空单元格：输出空 text-content，与官方引擎样本对齐
    // （避免缺省内容被解析为 null 导致渲染异常）。
    const el = node.ele('text-content');
    el.dat('');
  }

  // facade（始终存在；有映射时 type="mapping"，映射类型见 mapping-type）
  const f = node.ele('facade');
  const facade = cell.facade;
  if (facade) {
    // mappingType 缺省归 custom（对齐 IR 语义与结构校验）
    const mappingType = facade.mappingType ?? 'custom';
    f.att('type', 'mapping');
    f.att('mapping-type', mappingType);
    if (facade.showOriginalValueOnMappingFail !== undefined) {
      f.att('show-original-value-on-mapping-fail', facade.showOriginalValueOnMappingFail);
    }
    if (mappingType === 'dataset') {
      if (facade.datasetName) f.att('dataset', facade.datasetName);
      if (facade.valueField) f.att('value-field', facade.valueField);
      if (facade.labelField) f.att('label-field', facade.labelField);
    } else {
      for (const d of facade.customMappingData ?? []) {
        const de = f.ele('custom-mapping-data');
        de.att('label', d.label);
        de.att('value', d.value);
      }
    }
  } else {
    f.att('type', 'none');
  }

  // 录入控件（填报）
  if (cell.control) {
    const el = node.ele('control');
    el.att('type', cell.control.type);
    if (cell.control.visible !== undefined) el.att('visible', cell.control.visible);
    if (cell.control.enableEmpty !== undefined) el.att('enable-empty', cell.control.enableEmpty);
    if (cell.control.enableEdit !== undefined) el.att('enable-edit', cell.control.enableEdit);
    if (cell.control.enableCustom !== undefined) el.att('enable-custom', cell.control.enableCustom);
    if (cell.control.datePattern) el.att('date-pattern', cell.control.datePattern);
    if (cell.control.verify) {
      const v = el.ele('verify');
      v.att('type', cell.control.verify.type);
      if (cell.control.verify.minLength !== undefined) v.att('min-length', cell.control.verify.minLength);
      if (cell.control.verify.maxLength !== undefined) v.att('max-length', cell.control.verify.maxLength);
      if (cell.control.verify.min !== undefined) v.att('min', cell.control.verify.min);
      if (cell.control.verify.max !== undefined) v.att('max', cell.control.verify.max);
    }
    if (cell.control.binding) {
      const b = el.ele('binding');
      b.att('type', cell.control.binding.type);
      for (const d of cell.control.binding.customData ?? []) {
        const de = b.ele('data');
        de.att('label', d.label);
        de.att('value', d.value);
      }
    }
  }

  // 条件样式（隔行变色、阈值高亮等）：每个 CellRender 生成一个 <render-item>。
  // 对齐官方 baskreport-vue3 引擎结构。
  emitCellRender(node, cell.style?.render);
}

/**
 * 序列化条件样式。官方 XML 结构（baskreport-vue3 引擎，见示例
 * 12.条件渲染/1.根据条件显示不同背景色.xml）：
 *   <render-item name="...">
 *     <content name="background" scope="cell" bgcolor="#xxx"/>  或 name="color" color="#xxx"
 *     <condition left-expression="#" op="&gt;" right-expression="20000"/>
 *   </render-item>
 * 直接挂在 <cell> 下（非 specialconditions 包裹）。
 */
function emitCellRender(node: any, renders: CellRender[] | undefined) {
  if (!renders || renders.length === 0) return;
  for (const r of renders) {
    const item = node.ele('render-item');
    item.att('name', r.scope === 'row' ? '整行条件' : '单元格条件');
    const content = item.ele('content');
    if (r.bgcolor) {
      content.att('name', 'background');
      content.att('scope', r.scope ?? 'cell');
      content.att('bgcolor', r.bgcolor);
    } else if (r.color || r.bold || r.italic || r.underline) {
      content.att('name', 'color');
      content.att('scope', r.scope ?? 'cell');
      if (r.color) content.att('color', r.color);
      if (r.bold) content.att('bold', 'true');
      if (r.italic) content.att('italic', 'true');
      if (r.underline) content.att('underline', 'true');
    }
    const cond = item.ele('condition');
    cond.att('left-expression', r.leftExpression);
    cond.att('op', r.op);
    cond.att('right-expression', r.rightExpression ?? 'true');
  }
}

function emitCell(node: any, cell: CellIR) {
  const pos = resolveParent(cell); // 仅调用一次，闭包内复用
  const el = node.ele('cell');
  for (const spec of CELL_ATTRS) {
    const v = spec.get(cell, pos);
    const out = v !== undefined ? v : spec.omitWhenUndefined ? undefined : spec.fallback;
    if (out !== undefined) attr(el, spec.xml, out);
  }
  emitStyleAndContent(el, cell);
}

function emitRow(node: any, row: RowIR) {
  const el = node.ele('row');
  attr(el, 'num', row.num);
  // 引擎 RowDefinitionParser 对 height 缺失会 NPE（getAttrShortValue 解析 null），
  // 故始终输出 height（缺省 26）。
  attr(el, 'height', row.height ?? 26);
  if (row.band) attr(el, 'band', row.band);
  if (row.lock) attr(el, 'lock', true);
}

function emitCol(node: any, col: ColumnIR) {
  const el = node.ele('col');
  attr(el, 'num', col.num);
  // 与 row 同理，col 的 width 缺失也会触发数值解析 NPE，始终输出（缺省 80）。
  attr(el, 'width', col.width ?? 80);
  if (col.lock) attr(el, 'lock', true);
}

function emitDataset(
  node: any,
  ds: DatasetIR,
  datasources: DatasourceIR[] = [],
  datasetIdLookup?: Map<string, string>,
) {
  const el = node.ele('dataset');
  // BeanDataset：服务端已注册的 Java Bean 数据集，无 datasource、无 sql，仅声明名称与字段。
  // 对齐官方格式 <dataset type="bean" name="...">（参考 sample-center「数据监控中心」示例）。
  if (ds.type === 'bean') {
    attr(el, 'type', 'bean');
    attr(el, 'name', ds.name);
    for (const f of ds.fields) {
      const fe = el.ele('field');
      fe.att('name', f.name);
      fe.att('label', f.label ?? f.name);
      fe.att('type', f.type);
    }
    return;
  }
  // 关联到「服务端已配置数据源」时，序列化需对齐服务端的引用方式：
  //   - type 输出 server（而非内部 sql 标记）
  //   - 用 datasource-id 引用真实服务端 id（如 samplecenter-jdbc），
  //     不再输出内部别名 datasource（如 mysql），避免引擎找不到数据源。
  // 连接到服务端后，SQL/字段在同节点下照常声明，引擎据此在已配置连接上执行。
  const ref = datasources.find((d) => d.name === ds.datasource);
  // 服务端数据集：当关联数据源的 datasourceType 为 api/adapt/virtual 时，
  // 数据集 type 跟随数据源真实类型名输出，并附带定位所需的 datasetId。
  const serverDsType = ref?.datasourceType;
  if (serverDsType === 'api' || serverDsType === 'adapt' || serverDsType === 'virtual') {
    // 服务端数据集：type 输出其关联数据源的真实类型名（api/adapt/virtual），并输出定位数据集所需的
    // datasetId（对应 XML 的 dataset-id）。该 id 是派生信息：优先用调用方传入的 datasetIdLookup
    // （服务端反查结果）。不要求 IR 持久化 datasetId，也无需 LLM/用户手写。
    attr(el, 'type', serverDsType);
    // 组合键：数据源名 + NUL + 数据集名，与 generate.ts 写入端保持一致（不同数据源可同名）。
    const resolvedId = datasetIdLookup?.get(`${ds.datasource}\u0000${ds.name}`);
    if (resolvedId) attr(el, 'dataset-id', resolvedId);
    if (ref?.id) {
      attr(el, 'datasource-id', ref.id);
    } else {
      attr(el, 'datasource', ds.datasource);
    }
  } else {
    const isServer = !!ref?.id;
    if (isServer) {
      attr(el, 'type', 'server');
      attr(el, 'datasource-id', ref!.id!);
    } else {
      attr(el, 'type', ds.type);
      attr(el, 'datasource', ds.datasource);
    }
  }
  attr(el, 'name', ds.name);
  // readFieldRemark（是否读取字段备注）仅关联服务端 jdbc/virtual 数据源的数据集才有意义，
  // 其余（api/adapt/内置 bean 等）一律不输出。
  if (serverDsType === 'jdbc' || serverDsType === 'virtual')
    attr(el, 'readFieldRemark', ds.readFieldRemark ?? false);
  if (ds.type === 'sql' && ds.sql) {
    const sql = el.ele('sql');
    sql.dat(ds.sql);
  }
  for (const f of ds.fields) {
    const fe = el.ele('field');
    fe.att('name', f.name);
    fe.att('label', f.label ?? f.name);
    fe.att('type', f.type);
  }
}

function emitDatasource(node: any, d: DatasourceIR) {
  const el = node.ele('datasource');
  // 固定引用服务端已配置数据源（kind=server）：只写 id/type/datasource-type，
  // 引擎据此关联服务端连接，报表内不再重复定义连接信息。
  const dsType = (d.datasourceType as string) || 'jdbc';
  attr(el, 'datasource-type', dsType);
  attr(el, 'type', 'server');
  attr(el, 'id', d.id ?? '');
  if (d.validationQuery) attr(el, 'validation-query', d.validationQuery);
}

function emitPaper(node: any, p: PaperIR) {
  const el = node.ele('page-setting');
  for (const spec of PAPER_ATTRS) {
    const v = spec.get(p);
    attr(el, spec.xml, v !== undefined ? v : spec.fallback);
  }
  // 分栏（列分栏报表）：每个分栏生成一个 <column cell=".." number=".."/>。
  for (const c of p.columns ?? []) {
    const col = el.ele('column');
    attr(col, 'cell', c.cell);
    attr(col, 'number', c.number);
  }
}

function emitSetting(node: any, title: string, watermark?: WatermarkIR) {
  const el = node.ele('setting');
  attr(el, 'show-empty-cell-style', DEFAULTS.showEmptyCellStyle);
  attr(el, 'row-limit', DEFAULTS.rowLimit);
  attr(el, 'split-row-for-paging', DEFAULTS.splitRowForPaging);
  const preview = el.ele('preview');
  attr(preview, 'page-align', 'left');
  attr(preview, 'page-margin', 5);
  attr(preview, 'enable-refresh', false);
  attr(preview, 'cache-enable', true);
  attr(preview, 'enable-clean-cell-binging-data', false);
  attr(preview, 'refresh-interval', 0);
  attr(preview, 'enable-title', true);
  const t = preview.ele('title');
  t.dat(title);
  // 四个工具栏（导出/分页/打印/滚动条）——与样本一致
  const exportTool = preview.ele('tool');
  exportTool.att('type', 'export');
  exportTool.att('label', '导出');
  for (const type of ['excel', 'word', 'pdf', 'csv']) {
    const item = exportTool.ele('item');
    item.att('enable', true);
    item.att('type', type);
    item.att('icon', `pv pv-${type}`);
    item.att('export-page', true);
    item.att('export-page-label', '导出当前页');
    item.att('export-all', true);
    item.att('export-all-label', '导出所有');
    item.att('file-name-type', 'template');
    item.att('export-file-name', '');
    item.att('label', `导出${type.toUpperCase()}`);
    item.att('password', '');
  }
  const paging = preview.ele('tool');
  paging.att('type', 'paging');
  paging.att('show-first-page-tool', true);
  paging.att('show-prev-page-tool', true);
  paging.att('show-next-page-tool', true);
  paging.att('show-last-page-tool', true);
  paging.att('show-custom-page-tool', true);
  paging.att('first-page-label', '第一页');
  paging.att('prev-page-label', '上一页');
  paging.att('custom-page-label', '页码');
  paging.att('next-page-label', '下一页');
  paging.att('last-page-label', '尾页');
  const print = preview.ele('tool');
  print.att('type', 'print');
  print.att('label', '打印');
  print.att('print-page', true);
  print.att('silent-print-page', false);
  print.att('print-all', true);
  print.att('silent-print-all', false);
  print.att('print-web', true);
  print.att('print-web-label', '网页打印');
  print.att('print-page-label', '打印当前页');
  print.att('print-all-label', '打印所有页');
  print.att('silent-print-all-label', '无预览打印所有页');
  print.att('silent-print-page-label', '无预览打印当前页');
  const scroll = preview.ele('tool');
  scroll.att('type', 'changescroll');
  scroll.att('label', '滚动条');
  scroll.att('show-label', true);
  scroll.att('show-icon', true);
  // 水印（官方 <setting> 内的 <watermaker>，位于 preview 之后）。
  if (watermark) {
    const wm = el.ele('watermaker');
    attr(wm, 'left', watermark.left ?? 10);
    attr(wm, 'top', watermark.top ?? 5);
    attr(wm, 'rotate', watermark.rotate ?? -30);
    attr(wm, 'forecolor', watermark.forecolor ?? '#dddddd');
    attr(wm, 'font-size', watermark.fontSize ?? 40);
    attr(wm, 'font-family', watermark.fontFamily ?? '微软雅黑');
    attr(wm, 'bold', watermark.bold ?? false);
    attr(wm, 'italic', watermark.italic ?? false);
    attr(wm, 'underline', watermark.underline ?? false);
    wm.dat(`"${watermark.text}"`);
  }
}

/** 把 IR 参数类型映射为 BaskReport 参数类型名（首字母大写，如 String / Number / Date / Datetime / List）。缺省 String。 */
function toBaskType(type?: ParameterType): string {
  return (type && FIELD_TYPE_MAP[type]) || FIELD_TYPE_MAP.string;
}

/** 输出查询参数定义 `<parameter>`（BaskReport 真实格式：datatype + default-value）。 */
function emitParameter(node: any, p: ParameterIR) {
  const el = node.ele('parameter');
  attr(el, 'name', p.name);
  attr(el, 'datatype', toBaskType(p.type));
  attr(el, 'default-value', p.defaultValue ?? '');
}

/**
 * 输出查询表单 UI 元素 `<form-element>`（BaskReport 真实格式）。
 */
function emitFormElement(node: any, fe: FormElementIR) {
  const el = node.ele('form-element');
  attr(el, 'left', fe.left ?? -4);
  attr(el, 'top', fe.top ?? 36);
  attr(el, 'required', fe.required ?? false);
  attr(el, 'submit-on-change', fe.submitOnChange ?? false);
  attr(el, 'apply-prefix-suffix-when-empty', fe.applyPrefixSuffixWhenEmpty ?? false);
  attr(el, 'form-type', fe.formType);
  attr(el, 'name', fe.name);
  if (fe.paramType) attr(el, 'param-type', toBaskType((fe.paramType.toLowerCase()) as ParameterType));
  attr(el, 'label', fe.label ?? fe.name);
  attr(el, 'width', fe.width ?? 120);
  attr(el, 'editor-type', normalizeEditorType(fe.editorType));
  if (fe.binding) {
    const b = el.ele('binding');
    b.att('type', fe.binding.type);
    for (const d of fe.binding.customData ?? []) {
      const de = b.ele('data');
      de.att('label', d.label);
      de.att('value', d.value);
    }
  }
}

/** 输出填报配置 `<fill>`。 */
function emitFill(node: any, fill: NonNullable<ReportIR['fill']>) {
  const el = node.ele('fill');
  const s = el.ele('submit');
  attr(s, 'name', fill.submit.name);
  attr(s, 'type', fill.submit.type ?? 'sql');
  if (fill.submit.submitType) attr(s, 'submit-type', fill.submit.submitType);
  attr(s, 'datasource', fill.submit.datasource);
  attr(s, 'table', fill.submit.table);
  for (const f of fill.submit.fields) {
    const fe = s.ele('field');
    attr(fe, 'name', f.name);
    attr(fe, 'type', f.type);
    if (f.primaryKey) fe.att('primary-key', true);
    if (f.autoIncrement) fe.att('auto-increment', true);
    attr(fe, 'value-type', f.valueType ?? 'cell');
    attr(fe, 'value', f.value);
  }
}

/**
 * IR → BaskReport XML 字符串。纯函数。
 *
 * @param datasetIdLookup 可选。数据集名称 → 服务端 datasetId 的映射（由调用方按
 *   「数据源 id + 数据集名」从 buildDatabaseTables 反查得到）。提供时，API 类型数据集
 *   会输出对应 dataset-id；不提供时（如纯 XML 往返 parse→serialize），不输出 dataset-id——
 *   因为 datasetId 是服务端派生信息，本就不持久化在 IR 中，需经 generate 反查补全。
 */
export function serializeToXml(
  report: ReportIR,
  datasetIdLookup?: Map<string, string>,
): string {
  // 序列化前自动规范化顶层父格码（双向断开左/上父格交叉环，详见 normalizeTopParentCells）。
  const normalizedReport: ReportIR = {
    ...report,
    cells: normalizeTopParentCells(report.cells),
  };
  const grid = expandGrid(normalizedReport);
  const root: any = create({ version: '1.0', encoding: 'UTF-8' }).ele('baskreport');
  root.att('version', '1.1');

  // 单元格（仅输出非覆盖格），按 row 升序、col 升序
  const ordered = [...grid.cells]
    .filter((g) => !g.covered)
    .sort((a, b) => a.cell.row - b.cell.row || a.cell.col - b.cell.col);
  for (const g of ordered) emitCell(root, g.cell);

  // 行（按 num 升序）
  const rows = [...(normalizedReport.rows ?? [])].sort((a, b) => a.num - b.num);
  for (const r of rows) emitRow(root, r);
  // 补全缺省行
  for (let n = 1; n <= grid.rowCount; n++) {
    if (!rows.some((r) => r.num === n)) emitRow(root, { num: n });
  }

  // 列
  const cols = [...(normalizedReport.columns ?? [])].sort((a, b) => a.num - b.num);
  for (const c of cols) emitCol(root, c);
  for (let n = 1; n <= grid.columnCount; n++) {
    if (!cols.some((c) => c.num === n)) emitCol(root, { num: n });
  }

  // 数据集 / 数据源
  const datasources = normalizedReport.datasources ?? [];
  for (const ds of normalizedReport.datasets ?? []) emitDataset(root, ds, datasources, datasetIdLookup);
  for (const d of datasources) emitDatasource(root, d);

  // 参数表单元素：优先使用显式 parameterForm；
  // 否则由 parameters 自动推导（每个参数 → 一个 form-type=param 的 form-element）。
  // 查询按钮统一由下方「保底」逻辑补全，保证两条路径只由一处负责，逻辑唯一。
  let formElements: FormElementIR[] = normalizedReport.parameterForm ?? [];
  if (formElements.length === 0 && (normalizedReport.parameters ?? []).length > 0) {
    formElements = (report.parameters ?? []).map((p, i) => {
      const width = p.width ?? 120;
      // 缺省按文档算法自动横向排列：left = -4 + i*(width+66)，top = 36
      const left = p.left ?? -4 + i * (width + 66);
      const top = p.top ?? 36;
      // editor-type 默认策略：仅 date 类型默认使用 select（下拉日期选择器）；
      // 其余类型不在此处赋默认值，直接用用户显式指定的 p.editorType（emitFormElement 内部再兜底 text）。
      const editorType = p.type === 'date' && !p.editorType ? 'select' : p.editorType;
      return {
        name: p.name,
        formType: 'param',
        paramType: p.type, // IR 小写枚举，emitFormElement 内部会 toBaskType
        label: p.label ?? p.name,
        editorType,
        width,
        required: p.required,
        left,
        top,
        submitOnChange: p.submitOnChange,
        applyPrefixSuffixWhenEmpty: p.applyPrefixSuffixWhenEmpty,
        binding: p.binding,
      };
    });
  }
  // 保底：存在查询参数但缺少提交按钮时，统一补一个「查询」按钮。
  // 同时兜底两类情形：① 显式 parameterForm 漏写 submit；② 自动推导分支（仅 param、未带按钮）。
  const hasParam = formElements.some((fe) => fe.formType === 'param');
  const hasSubmit = formElements.some((fe) => fe.formType === 'submit');
  if (hasParam && !hasSubmit) {
    const lastRight = formElements.reduce(
      (acc, fe) => Math.max(acc, (fe.left ?? -4) + (fe.width ?? 120)),
      -4,
    );
    formElements.push({
      name: '查询',
      formType: 'submit',
      label: '查询',
      editorType: 'button',
      left: lastRight + 66,
      top: 36,
    });
  }
  for (const fe of formElements) emitFormElement(root, fe);
  // 参数定义
  for (const p of normalizedReport.parameters ?? []) emitParameter(root, p);

  // 填报
  if (normalizedReport.fill) emitFill(root, normalizedReport.fill);

  // 纸张
  emitPaper(root, normalizedReport.paper ?? {});

  // 设置 + 预览工具栏 + 水印
  // 预览标题：缺省 fileName()（文件名函数，不带引号）；
  // 自定义标题（非 fileName()）按引擎字符串字面量规范加双引号包裹，例如 "员工薪资报表"。
  const title =
    normalizedReport.title && normalizedReport.title !== 'fileName()'
      ? `"${normalizedReport.title}"`
      : normalizedReport.title ?? 'fileName()';
  emitSetting(root, title, normalizedReport.watermark);

  return root.end({ prettyPrint: false });
}

/** 斜线表头分区项几何信息（与 baskreport-js CrosstabItem 字段对齐）。 */
interface CrosstabItemGeo {
  text?: string;
  degree?: number;
  xPos?: number;
  yPos?: number;
}

/**
 * 计算斜线表头每个分区 <item> 的几何属性（degree / xPos / yPos）。
 * 移植自 baskreport-js CrosstabRender.calcItemDetail，纯函数、不依赖 canvas。
 * items 顺序：列维度标签在前、行维度标签在后（与 serialize 构造顺序一致）。
 * endPoint 为角区画布相对坐标（官方减去 2pt 边框余量后的 width-2 / height-2）。
 */
function calcItemDetail(
  items: CrosstabItemGeo[],
  endPoint: { xPos: number; yPos: number },
): void {
  const area = endPoint.xPos * endPoint.yPos;
  const space = area / items.length;
  const size = items.length / 2;
  const stepY = (2 * space) / endPoint.xPos;
  const stepX = (2 * space) / endPoint.yPos;

  for (let i = 0; i < items.length; i++) {
    const item = items[i];
    if (!item) continue;
    if (i < size) {
      item.xPos = endPoint.xPos;
      item.yPos = (i + 1) * stepY;
      const a = item.yPos;
      const b = item.xPos;
      const c = Math.sqrt(a * a + b * b);
      item.degree = (Math.acos(b / c) * 180) / Math.PI;
    } else {
      item.xPos = (items.length - i - 1) * stepX;
      item.yPos = endPoint.yPos;
      const a = item.xPos;
      const b = item.yPos;
      const c = Math.sqrt(a * a + b * b);
      item.degree = 90 - (Math.acos(b / c) * 180) / Math.PI;
    }
  }
}
